import React, { useState } from "react";
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Menu,
  MenuItem,
  IconButton,
  Divider,
  Box,
  useTheme,
  useMediaQuery,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemButton
} from "@mui/material";
import {
  AccountCircle,
  Settings,
  AdminPanelSettings,
  EmojiEvents,
  Devices,
  Analytics,
  People,
  Dashboard,
  Logout,
  Menu as MenuIcon
} from "@mui/icons-material";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "./auth/AuthContext";
import { BRANDING } from "../config/branding";

const Navbar = () => {
  const { user, isAdmin, signOut } = useAuth();
  const [anchorEl, setAnchorEl] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const location = useLocation();

  const handleUserMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleUserMenuClose = () => {
    setAnchorEl(null);
  };

  const handleSignOut = async () => {
    try {
      await signOut();
      handleUserMenuClose();
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const displayName = user?.attributes?.['custom:display_name'] || 
                     user?.attributes?.email?.split('@')[0] || 
                     'User';

  const navigationItems = [
    { text: 'Dashboard', path: '/', icon: <Dashboard /> },
    { text: 'Challenges', path: '/challenges', icon: <EmojiEvents /> },
    { text: 'Devices', path: '/devices', icon: <Devices /> },
    { text: 'Analytics', path: '/analytics', icon: <Analytics /> },
    { text: 'Friends', path: '/friends', icon: <People /> },
  ];

  const userMenuItems = [
    { text: 'Settings', path: '/my-account/settings', icon: <Settings /> },
    { text: 'Profile', path: '/my-account/info', icon: <AccountCircle /> },
    ...(isAdmin ? [{ text: 'Admin', path: '/my-account/admin', icon: <AdminPanelSettings /> }] : []),
  ];

  const isActiveRoute = (path) => {
    if (path === '/') {
      return location.pathname === '/';
    }
    return location.pathname.startsWith(path);
  };

  const MobileDrawer = (
    <Box sx={{ width: 250 }} role="presentation">
      <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider', textAlign: 'center' }}>
        <img 
          src="/images/nav_logo.png" 
          alt="WalkMore Logo"
          style={{
            maxWidth: '50px',
            maxHeight: '20px',
            width: 'auto',
            height: 'auto',
            objectFit: 'contain'
          }}
        />
      </Box>
      <List>
        {navigationItems.map((item) => (
          <ListItem key={item.text} disablePadding>
            <ListItemButton
              component={Link}
              to={item.path}
              selected={isActiveRoute(item.path)}
              onClick={() => setMobileOpen(false)}
            >
              <ListItemIcon>{item.icon}</ListItemIcon>
              <ListItemText primary={item.text} />
            </ListItemButton>
          </ListItem>
        ))}
        <Divider sx={{ my: 1 }} />
        {userMenuItems.map((item) => (
          <ListItem key={item.text} disablePadding>
            <ListItemButton
              component={Link}
              to={item.path}
              onClick={() => setMobileOpen(false)}
            >
              <ListItemIcon>{item.icon}</ListItemIcon>
              <ListItemText primary={item.text} />
            </ListItemButton>
          </ListItem>
        ))}
        <ListItem disablePadding>
          <ListItemButton onClick={handleSignOut}>
            <ListItemIcon><Logout /></ListItemIcon>
            <ListItemText primary="Sign Out" />
          </ListItemButton>
        </ListItem>
      </List>
    </Box>
  );

  return (
    <>
      <AppBar position="static" elevation={0}>
        <Toolbar>
          {isMobile && (
            <IconButton
              color="inherit"
              aria-label="open drawer"
              edge="start"
              onClick={handleDrawerToggle}
              sx={{ mr: 2 }}
            >
              <MenuIcon />
            </IconButton>
          )}
          
          <Box
            component={Link}
            to="/"
            sx={{
              flexGrow: isMobile ? 1 : 0,
              textDecoration: 'none',
              mr: 4,
              display: 'flex',
              alignItems: 'center'
            }}
          >
            <img 
              src="/images/nav_logo.png" 
              alt="WalkMore Logo"
              style={{
                maxHeight: '17px',
                width: 'auto',
                height: 'auto',
                objectFit: 'contain'
              }}
            />
          </Box>

          {!isMobile && (
            <Box sx={{ flexGrow: 1, display: 'flex', gap: 1 }}>
              {navigationItems.map((item) => (
                <Button
                  key={item.text}
                  component={Link}
                  to={item.path}
                  color="inherit"
                  startIcon={item.icon}
                  sx={{
                    textTransform: 'none',
                    fontWeight: isActiveRoute(item.path) ? 600 : 400,
                    backgroundColor: isActiveRoute(item.path) ? 'rgba(255, 255, 255, 0.1)' : 'transparent',
                    '&:hover': {
                      backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    }
                  }}
                >
                  {item.text}
                </Button>
              ))}
            </Box>
          )}

          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Button
              color="inherit"
              onClick={handleUserMenuOpen}
              startIcon={<AccountCircle />}
              sx={{ textTransform: 'none' }}
            >
              {displayName}
            </Button>
            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleUserMenuClose}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
            >
              {userMenuItems.map((item) => (
                <MenuItem
                  key={item.text}
                  component={Link}
                  to={item.path}
                  onClick={handleUserMenuClose}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {item.icon}
                    {item.text}
                  </Box>
                </MenuItem>
              ))}
              <Divider />
              <MenuItem onClick={handleSignOut}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Logout />
                  Sign Out
                </Box>
              </MenuItem>
            </Menu>
          </Box>
        </Toolbar>
      </AppBar>

      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true, // Better open performance on mobile.
        }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': { boxSizing: 'border-box', width: 250 },
        }}
      >
        {MobileDrawer}
      </Drawer>
    </>
  );
};

export default Navbar;

